import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, IconButton, useColorModeValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { apiGetHomepageBanners } from 'api';

interface Banner {
  id: number;
  image_url: string;
  link_url: string;
  order: number;
  duration_seconds: number;
}

const PLACEHOLDER_BANNER: Banner = {
  id: 0,
  image_url: '',
  link_url: '',
  order: 0,
  duration_seconds: 15,
};

export const PromoBanner: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bgColor = useColorModeValue('#f0f4ff', 'navy.800');

  const { data } = useQuery({
    queryKey: ['homepage-banners'],
    queryFn: () => apiGetHomepageBanners() as Promise<Banner[]>,
    staleTime: 5 * 60 * 1000,
  });

  const banners: Banner[] = data && data.length > 0 ? data : [PLACEHOLDER_BANNER];
  const total = banners.length;

  const startTimer = (idx = current) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (total > 1) {
      const ms = (banners[idx]?.duration_seconds ?? 15) * 1000;
      timerRef.current = setTimeout(() => {
        setCurrent((c) => {
          const next = (c + 1) % total;
          startTimer(next);
          return next;
        });
      }, ms);
    }
  };

  useEffect(() => {
    setCurrent(0);
    startTimer(0);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [data]);

  const goTo = (idx: number) => {
    const next = (idx + total) % total;
    setCurrent(next);
    startTimer(next);
  };

  const banner = banners[current];

  return (
    <Box
      position="relative"
      borderRadius="16px"
      overflow="hidden"
      w="100%"
      bg={bgColor}
    >
      {/* Slide image — natural height so nothing is cropped */}
      {banner.image_url ? (
        <Box
          as={banner.link_url ? 'a' : 'div'}
          {...(banner.link_url ? { href: banner.link_url, target: '_blank', rel: 'noopener noreferrer' } : {})}
          display="block"
          w="100%"
        >
          <img
            src={banner.image_url}
            alt="Banner"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>
      ) : (
        <Flex
          h="200px"
          alignItems="center"
          justifyContent="center"
          bg="linear-gradient(135deg, #e85d04 0%, #f48c06 100%)"
          flexDirection="column"
          gap="8px"
        >
          <Box fontSize="xl" fontWeight="bold" color="white" letterSpacing="wide" textAlign="center" px="20px">
            Promotional Banner
          </Box>
          <Box fontSize="sm" color="whiteAlpha.800" textAlign="center">
            Add banners via Settings → Homepage Banners
          </Box>
        </Flex>
      )}

      {/* Prev / Next arrows */}
      {total > 1 && (
        <>
          <IconButton
            aria-label="Previous banner"
            icon={<ChevronLeftIcon boxSize="6" />}
            position="absolute"
            left="8px"
            top="50%"
            transform="translateY(-50%)"
            size="sm"
            borderRadius="full"
            bg="blackAlpha.500"
            color="white"
            _hover={{ bg: 'blackAlpha.700' }}
            onClick={() => goTo(current - 1)}
            zIndex={2}
          />
          <IconButton
            aria-label="Next banner"
            icon={<ChevronRightIcon boxSize="6" />}
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            size="sm"
            borderRadius="full"
            bg="blackAlpha.500"
            color="white"
            _hover={{ bg: 'blackAlpha.700' }}
            onClick={() => goTo(current + 1)}
            zIndex={2}
          />
        </>
      )}

      {/* Dot indicators */}
      {total > 1 && (
        <Flex
          position="absolute"
          bottom="10px"
          left="50%"
          transform="translateX(-50%)"
          gap="6px"
          zIndex={2}
        >
          {banners.map((_, i) => (
            <Box
              key={i}
              w={i === current ? '20px' : '8px'}
              h="8px"
              borderRadius="full"
              bg={i === current ? 'white' : 'whiteAlpha.600'}
              transition="all 0.3s"
              cursor="pointer"
              onClick={() => goTo(i)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};
